import { useState } from "react";
import { Phone, Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BUSINESS } from "@/data/site";
import { Reveal } from "@/components/site/Reveal";

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");

export const Contact = ({ heading = true }) => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      // Netlify Forms: POST url-encoded data to the site root. No backend required.
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...form }),
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Sorry, something went wrong. Please call us on " + BUSINESS.phoneDisplay + ".");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-36 bg-surface border-t border-hairline">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        {/* Left: details */}
        <div className="lg:col-span-5">
          {heading && (
            <>
              <Reveal>
                <h2 className="font-heading uppercase leading-[0.9] tracking-tight text-4xl sm:text-6xl text-stark">
                  Need an<br />electrician in<br />Bradford?
                </h2>
              </Reveal>
              <p className="mt-8 text-concrete leading-relaxed max-w-md">
                Tell us what you need and we'll get straight back to you with honest advice and a free,
                no-obligation quote. For emergencies, call us directly — day or night.
              </p>
            </>
          )}

          <div className="mt-10 space-y-1">
            <a href={BUSINESS.phoneHref} data-testid="contact-phone" className="group flex items-center gap-4 py-4 border-t border-hairline">
              <Phone className="w-5 h-5 text-brand" strokeWidth={2} />
              <span className="font-heading text-2xl text-stark group-hover:text-brand transition-colors">{BUSINESS.phoneDisplay}</span>
            </a>
            <a href={BUSINESS.emailHref} data-testid="contact-email" className="group flex items-center gap-4 py-4 border-t border-hairline">
              <Mail className="w-5 h-5 text-brand" strokeWidth={2} />
              <span className="text-stark group-hover:text-brand transition-colors break-all">{BUSINESS.email}</span>
            </a>
            <div className="flex items-center gap-4 py-4 border-y border-hairline">
              <MapPin className="w-5 h-5 text-brand" strokeWidth={2} />
              <span className="text-concrete">{BUSINESS.address}</span>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="h-full min-h-[400px] flex flex-col items-center justify-center text-center rounded-3xl ring-1 ring-hairline border-t-4 border-t-brand bg-void p-12 shadow-[0_40px_90px_-30px_rgba(30,86,214,0.5)]">
              <CheckCircle2 className="w-16 h-16 text-brand mb-6" strokeWidth={1.5} />
              <h3 className="font-heading uppercase text-3xl text-stark tracking-tight">Enquiry sent</h3>
              <p className="text-concrete mt-3 max-w-sm">
                Thanks for getting in touch. We'll call you back as soon as we can. Need us urgently?
                Call {BUSINESS.phoneDisplay}.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="contact-reset"
                className="mt-8 text-brand font-medium hover:text-stark transition-colors"
              >
                Send another enquiry
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              className="relative overflow-hidden rounded-3xl bg-void ring-1 ring-hairline shadow-[0_40px_90px_-30px_rgba(30,86,214,0.5)]"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" onChange={() => {}} /></label>
              </p>
              <div className="relative overflow-hidden px-8 lg:px-12 py-9 bg-gradient-to-br from-[#16337A] via-[#0F2657] to-[#0A1B3F]">
                <div aria-hidden className="pointer-events-none absolute -top-10 -right-6 w-40 h-40 rounded-full bg-[#2F8BFF]/25 blur-3xl" />
                <p className="relative font-mono text-xs tracking-[0.25em] text-[#4DA3FF]">GET A FREE QUOTE</p>
                <h3 className="relative font-heading uppercase tracking-tight text-2xl sm:text-3xl text-[#2F8BFF] mt-2 text-pulse">Send us an enquiry</h3>
              </div>
              <div className="p-8 lg:p-12 space-y-7">
              <div>
                <label htmlFor="name" className="block font-mono text-xs tracking-[0.2em] text-concrete mb-3">YOUR NAME</label>
                <input
                  id="name"
                  name="name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="e.g. Jane Smith"
                  className="w-full bg-transparent border-b border-hairline focus:border-brand py-3 text-lg text-stark placeholder:text-concrete/50 outline-none transition-colors"
                  required
                />
              </div>
              <div>
                <label htmlFor="phone" className="block font-mono text-xs tracking-[0.2em] text-concrete mb-3">PHONE NUMBER</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="e.g. 07000 000000"
                  className="w-full bg-transparent border-b border-hairline focus:border-brand py-3 text-lg text-stark placeholder:text-concrete/50 outline-none transition-colors"
                  required
                />
              </div>
              <div>
                <label htmlFor="message" className="block font-mono text-xs tracking-[0.2em] text-concrete mb-3">HOW CAN WE HELP?</label>
                <textarea
                  id="message"
                  name="message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Briefly describe the job — e.g. fuse board keeps tripping, need a rewire quote..."
                  className="w-full bg-transparent border-b border-hairline focus:border-brand py-3 text-lg text-stark placeholder:text-concrete/50 outline-none transition-colors resize-none"
                  required
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 bg-brand text-white font-semibold text-lg px-8 py-5 rounded-xl shadow-lg shadow-brand/40 hover:bg-brand-dim hover:shadow-xl hover:scale-[1.01] active:scale-95 transition-all duration-200 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                {loading ? "Sending..." : "Send Enquiry"}
              </button>
              <p className="text-xs text-concrete text-center">No spam. We only use your details to reply to your enquiry.</p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
